/**
 * "Provided" revenue cell for the gated Hierarchy editor (PLT-5692): the raw
 * revenue share the user entered, shown and edited as a plain 0..1 number (NOT a
 * percentage) with at most 3 decimals (PLT-5799 — Roni). Single-click to edit
 * inline (Enter / blur commits, Esc cancels); the value round-trips as the stored
 * 0..1 fraction verbatim (no ×100 percentage conversion). Read-only consumers
 * render the same plain formatted value.
 */
import { TextInput } from '@primer/react';
import { useState } from 'react';
import styled from 'styled-components';
import { PiuProductState } from '@upright/common/model/input/companies/products/product-input-ui/types';

interface ProvidedRevenueCellProps {
  product: PiuProductState;
  editable: boolean;
  onCommit: (product: PiuProductState, revenueShare: number | undefined) => void;
}

// Render the stored 0..1 revenue share as a plain number (not a percentage), with at most 3
// decimals and no trailing zeros (PLT-5799 — Roni): 0.315 → "0.315", 0.3 → "0.3", 0.5 → "0.5".
function formatShare(share: number): string {
  return parseFloat(share.toFixed(3)).toString();
}

export function ProvidedRevenueCell({ product, editable, onCommit }: ProvidedRevenueCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const hasValue = product.revenueShare !== undefined;

  if (!editable) {
    return <ReadOnlyValue>{hasValue ? formatShare(product.revenueShare as number) : 'n/a'}</ReadOnlyValue>;
  }

  if (!editing) {
    return (
      <EditableValue
        title="Click to edit"
        onClick={event => {
          event.stopPropagation();
          // Edit the raw 0..1 value directly (no ×100), trimmed to at most 3 decimals for a clean start.
          setDraft(hasValue ? String(parseFloat((product.revenueShare as number).toFixed(3))) : '');
          setEditing(true);
        }}
      >
        {hasValue ? formatShare(product.revenueShare as number) : <Placeholder>Add</Placeholder>}
      </EditableValue>
    );
  }

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed === '') {
      onCommit(product, undefined);
      return;
    }
    const parsed = Number(trimmed);
    // Ignore invalid / negative input — revert to the previous value.
    if (!Number.isFinite(parsed) || parsed < 0) return;
    // The user types the raw 0..1 share directly (no ×100 percentage conversion, PLT-5799).
    onCommit(product, parsed);
  };

  return (
    <TextInput
      type="number"
      autoFocus
      min={0}
      value={draft}
      aria-label="Provided revenue share"
      onClick={event => {
        event.stopPropagation();
      }}
      onChange={event => {
        setDraft(event.target.value);
      }}
      onBlur={commit}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          setEditing(false);
        }
      }}
      // Hide the native number-input spin buttons: revenue figures aren't stepped up/down, and the
      // 0.001-step arrows nudged by a distracting tiny amount (PLT-5841 — Jenna). Keep type="number"
      // for the numeric keyboard + `min` handling; commit() still validates the entered value.
      sx={{
        width: '7em',
        '& input': { MozAppearance: 'textfield' },
        '& input::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
        '& input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
      }}
    />
  );
}

const ReadOnlyValue = styled.span`
  color: var(--fgColor-default);
  white-space: nowrap;
`;

const EditableValue = styled.span`
  cursor: text;
  white-space: nowrap;
  padding: 2px 4px;
  border-radius: var(--border-radius__sm);
  &:hover {
    background-color: var(--bgColor-neutral-muted, rgba(0, 0, 0, 0.05));
  }
`;

const Placeholder = styled.span`
  color: var(--fgColor-muted);
`;
